import {
  chooseTrack,
  LAST_START_TRACK_KEY,
  RADIO_FADE_SECONDS,
  RADIO_MASTER_GAIN,
  RADIO_TRACKS,
} from './tracks';

const FADE_STEPS = 64;
const AUDIO_START_TIMEOUT_MS = 10000;
const CROSSFADE_START_TIMEOUT_MS = 4000;
const CONTEXT_RESUME_TIMEOUT_MS = 1000;

function withTimeout(promise, milliseconds) {
  let timer;
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error('Radio audio start timed out')), milliseconds);
    }),
  ]).finally(() => clearTimeout(timer));
}

function fadeCurve(direction, level) {
  return Float32Array.from({ length: FADE_STEPS }, (_, index) => {
    const angle = (index / (FADE_STEPS - 1)) * Math.PI / 2;
    return (direction === 'in' ? Math.sin(angle) : Math.cos(angle)) * level;
  });
}

function readLastStart() {
  try {
    return window.localStorage.getItem(LAST_START_TRACK_KEY);
  } catch (_error) {
    return null;
  }
}

function rememberStart(id) {
  try {
    window.localStorage.setItem(LAST_START_TRACK_KEY, id);
  } catch (_error) {
    // Playback still works when storage is unavailable.
  }
}

export default class RadioPlayer {
  constructor(onChange) {
    this.onChange = onChange;
    this.active = false;
    this.status = 'idle';
    this.current = null;
    this.decks = [];
    this.context = null;
    this.master = null;
    this.transition = null;
    this.autoFailure = null;
    this.playedTracks = new Set();
    this.session = 0;
    this.request = 0;
  }

  snapshot() {
    return { status: this.status, trackId: this.current?.track?.id || null };
  }

  emit(status = this.status) {
    this.status = status;
    this.onChange(this.snapshot());
  }

  createDeck(track) {
    const audio = new Audio();
    audio.preload = 'auto';
    const deck = { audio, track: null, source: null, gain: null };
    audio.addEventListener('timeupdate', () => this.onTimeUpdate(deck));
    audio.addEventListener('ended', () => this.onEnded(deck));
    audio.addEventListener('error', () => {
      if (this.active && this.current === deck) this.emit('error');
    });
    this.setTrack(deck, track);
    return deck;
  }

  setTrack(deck, track) {
    if (!track || deck.track?.id === track.id) return;
    deck.audio.pause();
    deck.track = track;
    deck.audio.src = track.src;
    deck.audio.load();
  }

  activate() {
    if (this.active) return;
    this.active = true;
    this.session += 1;
    this.autoFailure = null;
    this.playedTracks.clear();
    const first = chooseTrack(RADIO_TRACKS, readLastStart());
    if (!first) {
      this.emit('error');
      return;
    }
    rememberStart(first.id);
    this.decks = [this.createDeck(first), this.createDeck(chooseTrack(RADIO_TRACKS, first.id))];
    this.current = this.decks[0];
    this.emit('ready');
    this.play();
  }

  ensureContext() {
    if (this.context) return this.context;
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) throw new Error('Web Audio is unavailable');
    const context = new AudioContextClass();
    const master = context.createGain();
    master.gain.value = RADIO_MASTER_GAIN;
    master.connect(context.destination);
    this.decks.forEach(deck => {
      deck.source = context.createMediaElementSource(deck.audio);
      deck.gain = context.createGain();
      deck.gain.gain.value = deck === this.current ? deck.track.gain : 0;
      deck.source.connect(deck.gain);
      deck.gain.connect(master);
    });
    this.context = context;
    this.master = master;
    return context;
  }

  primeStandby() {
    const standby = this.decks.find(deck => deck !== this.current);
    if (!standby || this.transition) return;
    // Safari grants media playback permission per element. Prime both decks
    // in the same user gesture; the standby deck is routed through a zero gain.
    standby.audio.play().then(() => {
      if (!this.transition || this.transition.to !== standby) {
        standby.audio.pause();
        standby.audio.currentTime = 0;
      }
    }).catch(() => {});
  }

  async play() {
    if (!this.active || !this.current || this.status === 'playing') return;
    const session = this.session;
    const request = ++this.request;
    this.emit('loading');
    try {
      const context = this.ensureContext();
      const resume = withTimeout(context.resume(), CONTEXT_RESUME_TIMEOUT_MS);
      const started = withTimeout(this.current.audio.play(), AUDIO_START_TIMEOUT_MS);
      this.primeStandby();
      await Promise.all([started, resume]);
      if (session !== this.session || request !== this.request) {
        this.current?.audio.pause();
        return;
      }
      if (context.state !== 'running') throw new Error('Playback requires a user gesture');
      this.playedTracks.add(this.current.track.id);
      this.emit('playing');
    } catch (error) {
      if (session !== this.session || request !== this.request) return;
      this.decks.forEach(deck => deck.audio.pause());
      this.emit(error?.name === 'NotAllowedError' || this.context?.state === 'suspended' ? 'blocked' : 'error');
    }
  }

  setGain(deck, value) {
    if (!deck?.gain || !this.context) return;
    const now = this.context.currentTime;
    deck.gain.gain.cancelScheduledValues(now);
    deck.gain.gain.setValueAtTime(value, now);
  }

  pause() {
    if (!this.active) return;
    this.request += 1;
    if (this.transition) this.finishTransition(false);
    this.decks.forEach(deck => deck.audio.pause());
    this.context?.suspend().catch(() => {});
    this.emit('paused');
  }

  selectTrack(id) {
    const track = RADIO_TRACKS.find(item => item.id === id);
    if (!track || !this.active || this.status === 'loading' || this.status === 'switching') return;
    if (this.current?.track?.id === id) return;
    if (this.status !== 'playing') {
      this.setTrack(this.current, track);
      this.setGain(this.current, track.gain);
      this.prepareStandby();
      this.emit('paused');
      return;
    }
    this.crossfade(track, 2.5);
  }

  prepareStandby() {
    const standby = this.decks.find(deck => deck !== this.current);
    if (standby) {
      this.setGain(standby, 0);
      this.setTrack(standby, this.nextUnplayedTrack());
    }
  }

  nextUnplayedTrack() {
    const currentId = this.current?.track?.id;
    let choices = RADIO_TRACKS.filter(track => track.id !== currentId && !this.playedTracks.has(track.id));
    if (!choices.length) {
      this.playedTracks = new Set(currentId ? [currentId] : []);
      choices = RADIO_TRACKS.filter(track => track.id !== currentId);
    }
    return chooseTrack(choices, currentId);
  }

  preparedNextTrack() {
    return this.decks.find(deck => deck !== this.current)?.track ||
      this.nextUnplayedTrack();
  }

  async crossfade(track, seconds = RADIO_FADE_SECONDS, automatic = false) {
    if (!this.active || this.transition || !track || track.id === this.current?.track?.id) return;
    const from = this.current;
    const to = this.decks.find(deck => deck !== from);
    const session = this.session;
    this.setTrack(to, track);
    this.setGain(to, 0);
    const transition = { from, to, timer: null, started: false };
    this.transition = transition;
    this.emit('switching');

    try {
      await withTimeout(to.audio.play(), CROSSFADE_START_TIMEOUT_MS);
      if (session !== this.session || this.transition !== transition) {
        to.audio.pause();
        return;
      }
      transition.started = true;
      if (from.audio.ended) {
        this.finishTransition(true);
        return;
      }
      const remaining = from.audio.duration - from.audio.currentTime;
      const fadeSeconds = Number.isFinite(remaining) ? Math.min(seconds, Math.max(0.1, remaining)) : seconds;
      const now = this.context.currentTime;
      transition.endTime = now + fadeSeconds;
      from.gain.gain.cancelScheduledValues(now);
      to.gain.gain.cancelScheduledValues(now);
      from.gain.gain.setValueAtTime(from.track.gain, now);
      to.gain.gain.setValueAtTime(0, now);
      from.gain.gain.setValueCurveAtTime(fadeCurve('out', from.track.gain), now, fadeSeconds);
      to.gain.gain.setValueCurveAtTime(fadeCurve('in', to.track.gain), now, fadeSeconds);
      transition.timer = setTimeout(() => this.finishTransition(true), fadeSeconds * 1000 + 100);
    } catch (error) {
      if (session !== this.session || this.transition !== transition) return;
      this.transition = null;
      to.audio.pause();
      this.setGain(from, from.track.gain);
      const failureStatus = error?.name === 'NotAllowedError' ? 'blocked' : 'error';
      if (from.audio.ended) {
        this.advanceAfterFailure(track, failureStatus);
      } else {
        if (automatic) this.autoFailure = { fromId: from.track.id, track, status: failureStatus };
        this.emit('playing');
      }
    }
  }

  advanceAfterFailure(track, status) {
    this.autoFailure = null;
    this.setTrack(this.current, track);
    this.setGain(this.current, track.gain);
    this.prepareStandby();
    this.emit(status);
  }

  finishTransition(playing) {
    const transition = this.transition;
    if (!transition) return;
    clearTimeout(transition.timer);
    this.transition = null;
    const { from, to } = transition;
    this.setGain(from, 0);
    this.setGain(to, to.track.gain);
    from.audio.pause();
    from.audio.currentTime = 0;
    this.current = to;
    if (transition.started) this.playedTracks.add(to.track.id);
    this.autoFailure = null;
    this.prepareStandby();
    this.emit(playing ? 'playing' : 'paused');
  }

  onTimeUpdate(deck) {
    if (this.transition?.to === deck && this.transition.started &&
        this.context.currentTime >= this.transition.endTime) {
      this.finishTransition(true);
    }
    if (!this.active || deck !== this.current || this.transition || this.status !== 'playing') return;
    if (this.autoFailure?.fromId === deck.track.id) return;
    const remaining = deck.audio.duration - deck.audio.currentTime;
    if (Number.isFinite(remaining) && remaining <= deck.track.transitionBeforeEnd) {
      this.crossfade(this.preparedNextTrack(), RADIO_FADE_SECONDS, true);
    }
  }

  onEnded(deck) {
    if (!this.active || deck !== this.current) return;
    if (this.transition) {
      if (this.transition.started) this.finishTransition(true);
      return;
    }
    if (this.autoFailure?.fromId === deck.track.id) {
      this.advanceAfterFailure(this.autoFailure.track, this.autoFailure.status);
      return;
    }
    if (this.status === 'playing') {
      this.crossfade(this.preparedNextTrack(), 1.5);
    }
  }

  deactivate() {
    if (!this.active) return;
    this.active = false;
    this.autoFailure = null;
    this.session += 1;
    this.request += 1;
    if (this.transition) {
      clearTimeout(this.transition.timer);
      this.transition = null;
    }
    this.decks.forEach(deck => {
      deck.audio.pause();
      deck.audio.removeAttribute('src');
      deck.audio.load();
      deck.source?.disconnect();
      deck.gain?.disconnect();
    });
    this.master?.disconnect();
    this.context?.close().catch(() => {});
    this.decks = [];
    this.current = null;
    this.playedTracks.clear();
    this.context = null;
    this.master = null;
    this.emit('idle');
  }
}
